import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// RBAC: can(module, action) from permissions
const can = (permissions, module, action, role) => {
  const r = (role || '').toLowerCase().replace(/\s+/g, '_');
  if (r === 'super_admin') return true;

  const hasPerms = permissions && typeof permissions === 'object' && Object.keys(permissions).length > 0;
  if (!hasPerms) {
    if (r === 'admin') return true;
    return false;
  }
  if (!permissions[module]) return false;
  return !!permissions[module][action];
};

export const usePermissions = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || {};
  return {
    permissions,
    can: (module, action) => can(permissions, module, action, user?.role)
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem('superadmin_token');
    if (!token) return null;
    try {
      const response = await api.get('/auth/profile', {
        params: { _: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      const profile = response.data?.data;
      if (profile) {
        setUser(profile);
        localStorage.setItem('superadmin_user', JSON.stringify(profile));
        return profile;
      }
    } catch (e) {
      console.error('Refresh profile failed:', e);
    }
    return null;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('superadmin_token');
    const storedUser = localStorage.getItem('superadmin_user');

    if (token && storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      refreshProfile().then((p) => {
        if (!p) setUser(parsed);
      });
    }
    setLoading(false);
  }, [refreshProfile]);

  // Step 1: Validate credentials (MFA optional based on server settings)
  const completeLogin = async (data) => {
    if (data?.token) {
      localStorage.setItem('superadmin_token', data.token);
      localStorage.setItem('superadmin_user', JSON.stringify(data.admin));
      setUser(data.admin);
      if (!data.requirePasswordChange && !data.admin?.must_change_password) {
        const profile = await refreshProfile();
        if (profile) setUser(profile);
      }
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const payload = response.data;

    if (payload.success && payload.data?.token) {
      await completeLogin(payload.data);
    }

    return payload;
  };

  const selectMfaMethod = async (sessionId, method) => {
    const response = await api.post('/auth/mfa/select', { sessionId, method });
    return response.data;
  };

  const verifyMfa = async (sessionId, method, code) => {
    const response = await api.post('/auth/verify-mfa', { sessionId, method, code });
    if (response.data.success && response.data.data?.token) {
      await completeLogin(response.data.data);
    }
    return response.data;
  };

  const resendMfaEmail = async (sessionId) => {
    const response = await api.post('/auth/mfa/resend-email', { sessionId });
    return response.data;
  };

  // Backward-compatible OTP helpers
  const verifyOTP = async (adminId, otp) => verifyMfa(adminId, 'email_otp', otp);
  const resendOTP = async (sessionId) => resendMfaEmail(sessionId);

  const logout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('superadmin_user');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    selectMfaMethod,
    verifyMfa,
    resendMfaEmail,
    verifyOTP,
    resendOTP,
    logout,
    refreshProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
